import React from "react";

const baseBtn =
  "rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm font-medium " +
  "text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 " +
  "dark:border-white/20 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700/80";

/**
 * Paginación server-side: Primera / Anterior / números / Siguiente / Última + rango "X–Y de Z".
 */
export default function ServerTablePagination({
  page,
  totalPages,
  total,
  pageSize,
  onPageChange,
  /** Texto plural descriptivo, ej. "CAFs" o "secuencias" */
  noun = "registros",
  /** Nota bajo el rango (ej. orden en servidor) */
  subtext = null,
}) {
  const tp = Math.max(0, Number(totalPages) || 0);
  const p = Math.max(1, Number(page) || 1);
  const t = Math.max(0, Number(total) || 0);
  const size = Math.max(1, Number(pageSize) || 1);

  const from = t === 0 ? 0 : (p - 1) * size + 1;
  const to = Math.min(p * size, t);

  const windowSize = 7;
  const pages = React.useMemo(() => {
    if (tp <= 0) return [];
    if (tp <= windowSize) {
      return Array.from({ length: tp }, (_, i) => i + 1);
    }
    let start = Math.max(1, p - Math.floor(windowSize / 2));
    const end = Math.min(tp, start + windowSize - 1);
    if (end - start + 1 < windowSize) {
      start = Math.max(1, end - windowSize + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [tp, p]);

  return (
    <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4 dark:border-white/10 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <div>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {t === 0 ? (
            "Sin resultados."
          ) : (
            <>
              Mostrando{" "}
              <span className="font-semibold tabular-nums text-navy-700 dark:text-white">
                {from}
              </span>
              –
              <span className="font-semibold tabular-nums text-navy-700 dark:text-white">
                {to}
              </span>{" "}
              de{" "}
              <span className="font-semibold tabular-nums text-navy-700 dark:text-white">
                {t}
              </span>{" "}
              {noun}
              {tp > 1 ? (
                <>
                  {" "}
                  · página{" "}
                  <span className="tabular-nums">{p}</span> /{" "}
                  <span className="tabular-nums">{tp}</span>
                </>
              ) : null}
            </>
          )}
        </p>
        {subtext ? (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{subtext}</p>
        ) : null}
      </div>

      {tp > 1 ? (
        <nav
          className="flex flex-wrap items-center gap-1"
          aria-label="Paginación de tabla"
        >
          <button
            type="button"
            className={baseBtn}
            disabled={p <= 1}
            onClick={() => onPageChange(1)}
          >
            Primera
          </button>
          <button
            type="button"
            className={baseBtn}
            disabled={p <= 1}
            onClick={() => onPageChange(p - 1)}
          >
            Anterior
          </button>
          {pages.map((num) => (
            <button
              key={num}
              type="button"
              className={`${baseBtn} min-w-[2.25rem] px-2 text-center ${
                num === p
                  ? "border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-900/35"
                  : ""
              }`}
              disabled={num === p}
              onClick={() => onPageChange(num)}
              aria-current={num === p ? "page" : undefined}
            >
              {num}
            </button>
          ))}
          <button
            type="button"
            className={baseBtn}
            disabled={p >= tp}
            onClick={() => onPageChange(p + 1)}
          >
            Siguiente
          </button>
          <button
            type="button"
            className={baseBtn}
            disabled={p >= tp}
            onClick={() => onPageChange(tp)}
          >
            Última
          </button>
        </nav>
      ) : null}
    </div>
  );
}
